<template>
  <!-- Модальне вікно для запиту доступу -->
  <teleport to="body" v-if="show">
    <div class="access-modal-overlay" @click.self="close">
      <div class="access-modal-container">
        <!-- Заголовок -->
        <div class="access-modal-header">
          <div class="access-modal-header-content">
            <div class="access-modal-icon-wrapper">
              <i class="bx bx-lock-open-alt"></i>
            </div>
            <div class="access-modal-header-text">
              <h3 class="access-modal-title">Запит доступу до компанії</h3>
              <!-- <p class="access-modal-code">ID запиту: #REQ-{{ requestId }}</p> -->
            </div>
          </div>
          <button class="access-modal-close" @click="close">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="access-modal-info">
          <p>Оберіть компанію, номер телефону та параметри доступу.</p>
        </div>

        <!-- Тіло -->
        <div class="access-modal-body">
          <!-- Вибір компанії -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-building text-success"></i>
              <h4 class="access-section-title">
                Виберіть компанію, до якої необхідний доступ
              </h4>
            </div>
            <div class="company-select-list">
              <div
                v-for="phone in userPhones"
                :key="phone.companyId"
                :class="[
                  'company-select-item',
                  { active: form.companyId === phone.companyId },
                ]"
                @click="selectCompany(phone)"
              >
                <div class="company-select-info">
                  <div class="company-select-name">
                    <i class="bx bx-buildings"></i>
                    {{ phone.companyName }}
                  </div>
                  <div class="company-select-phone">
                    <i class="bx bx-phone"></i>
                    +{{ phone.number }}
                  </div>
                </div>
                <i class="bx bx-check company-select-check"></i>
              </div>
              <div
                v-if="!userPhones || userPhones.length === 0"
                class="company-select-empty"
              >
                <i class="bx bx-info-circle"></i>
                <p>Немає доступних компаній</p>
              </div>
            </div>
          </div>

          <!-- Розділи з правами доступу -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-category text-primary"></i>
              <h4 class="access-section-title">Права доступу до розділів</h4>
              <a class="access-select-all" @click="setAllAccess('view')">
                Повний доступ до всіх
              </a>
            </div>
            <div class="access-permissions-list">
              <div
                v-for="section in sections"
                :key="section.value"
                class="access-permission-item"
              >
                <div class="access-permission-header">
                  <!-- <i :class="section.icon"></i> -->
                  <span class="access-permission-label">{{
                    section.label
                  }}</span>
                </div>
                <div
                  class="access-permission-options"
                  :class="{
                    'access-permission-options-two':
                      section.value === 'cashbox',
                  }"
                >
                  <div
                    v-for="type in getAvailableAccessTypes(section.value)"
                    :key="type.value"
                    :class="[
                      'access-permission-option',
                      {
                        active:
                          form.accessRights.simplePerms[section.value] ===
                          type.value,
                      },
                    ]"
                    @click="
                      form.accessRights.simplePerms[section.value] = type.value
                    "
                  >
                    <i :class="type.icon"></i>
                    <span>{{ type.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Тривалість доступу -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-time text-info"></i>
              <h4 class="access-section-title">Тривалість доступу</h4>
            </div>
            <div class="access-duration-grid">
              <div
                v-for="duration in durations"
                :key="duration.value"
                :class="[
                  'access-duration-card',
                  { active: form.accessDuration === duration.value },
                ]"
                @click="form.accessDuration = duration.value"
              >
                <div class="access-duration-number">
                  {{ duration.value }}
                </div>
                <div class="access-duration-label">годин</div>
              </div>
            </div>
          </div>

          <!-- Коментар -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-message-square-detail"></i>
              <h4 class="access-section-title">Коментар</h4>
              <span class="access-optional">необов'язково</span>
            </div>
            <textarea
              v-model="form.comment"
              class="access-comment-input"
              rows="3"
              placeholder="Додайте коментар або опис проблеми, яку потрібно вирішити..."
            ></textarea>
          </div>

          <!-- Попередження -->
          <div class="access-warning">
            <i class="bx bx-info-circle"></i>
            <div>
              <strong>Важлива інформація:</strong> Доступ буде автоматично
              відкликано після закінчення вказаного терміну. Всі дії спеціаліста
              будуть записані в журнал подій та доступні для перегляду.
            </div>
          </div>
        </div>

        <!-- Футер -->
        <div class="access-modal-footer">
          <button
            type="button"
            class="access-btn access-btn-cancel"
            @click="close"
          >
            Скасувати
          </button>
          <button
            type="button"
            class="access-btn access-btn-submit"
            @click="submit"
            :disabled="!isFormValid || isSubmitting"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {{ isSubmitting ? "Надсилання..." : "Надіслати запит" }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { storeS } from "@/store";
import { AccessClient } from "@/API.js";

const accessAPI = new AccessClient();

export default {
  props: ["show", "obj"],
  data() {
    return {
      // Дані форми запиту доступу
      form: {
        ticketId: "",
        companyId: "",
        accessRights: {
          simplePerms: {
            cashbox: "without",
            finance: "without",
            marketing: "without",
            products: "without",
            programs: "without",
            settings: "without",
            statistics: "without",
          },
        },
        accessDuration: 6,
        comment: "",
      },

      // Стан відправки
      isSubmitting: false,

      // Типи доступу для розділів
      accessTypes: [
        {
          value: "without",
          label: "Без доступу",
          icon: "bx bx-block",
          color: "#74788d",
        },
        {
          value: "view",
          label: "Перегляд",
          icon: "bx bx-show",
          color: "#50a5f1",
        },
        {
          value: "full",
          label: "Повний доступ",
          icon: "bx bx-lock-open",
          color: "#34c38f",
        },
      ],

      // Доступні розділи
      sections: [
        { value: "cashbox", label: "Каса", icon: "bx bx-money" },
        { value: "finance", label: "Фінанси", icon: "bx bx-wallet" },
        { value: "marketing", label: "Маркетинг", icon: "bx bx-bullhorn" },
        { value: "products", label: "Товари", icon: "bx bx-package" },
        { value: "programs", label: "Програма", icon: "bx bx-laptop" },
        { value: "settings", label: "Налаштування", icon: "bx bx-cog" },
        { value: "statistics", label: "Статистика", icon: "bx bx-bar-chart" },
      ],

      // Тривалість доступу (в годинах)
      durations: [
        { value: 6, label: "6 годин" },
        // { value: 8, label: "8 годин" },
        // { value: 12, label: "12 годин" },
      ],
    };
  },
  computed: {
    hasAnyAccess() {
      return Object.values(this.form.accessRights.simplePerms).some(
        (access) => access !== "without"
      );
    },
    userPhones() {
      const phones = this.obj?.userPhones || [];
      // Фільтруємо тільки ті елементи, у яких є компанія
      return phones.filter((phone) => phone.companyId && phone.companyName);
    },
    isFormValid() {
      return this.form.companyId && this.hasAnyAccess;
    },
  },
  created() {
    console.log("ths", this);
  },
  methods: {
    getAvailableAccessTypes(sectionValue) {
      // Для cashbox дозволені тільки "without" та "full"
      if (sectionValue === "cashbox") {
        return this.accessTypes.filter(
          (type) => type.value === "without" || type.value === "full"
        );
      } else {
        return this.accessTypes.filter(
          (type) => type.value === "without" || type.value === "view"
        );
      }
      // Для всіх інших розділів доступні всі типи
      // return this.accessTypes;
    },

    close() {
      this.$emit("close");
      // Скидаємо форму при закритті
      this.resetForm();
    },

    resetForm() {
      this.form = {
        ticketId: "",
        companyId: "",
        companyName: "",
        phone: "",
        accessRights: {
          simplePerms: {
            cashbox: "without",
            finance: "without",
            marketing: "without",
            products: "without",
            programs: "without",
            settings: "without",
            statistics: "without",
          },
        },
        accessDuration: 6,
        comment: "",
      };
    },

    setAllAccess(accessType) {
      Object.keys(this.form.accessRights.simplePerms).forEach((key) => {
        // Для cashbox встановлюємо "full" замість "view"
        if (key === "cashbox" && accessType === "view") {
          this.form.accessRights.simplePerms[key] = "full";
        } else {
          this.form.accessRights.simplePerms[key] = accessType;
        }
      });
    },

    selectCompany(item) {
      this.form.companyId = item.companyId;
      this.form.companyName = item.companyName;
      this.form.phone = item.number;
      console.log("Selected company:", item);
    },

    async submit() {
      try {
        this.isSubmitting = true;

        // Отримуємо ticketId
        const ticketId = this.obj.ticketId;

        if (!ticketId) {
          this.$toast.error("ID тікету не знайдено");
          this.isSubmitting = false;
          return;
        }

        if (!this.form.companyId) {
          this.$toast.error("Оберіть компанію");
          this.isSubmitting = false;
          return;
        }

        // Встановлюємо ticketId у форму
        this.form.ticketId = ticketId;

        console.log("Sending access request:", this.form);

        // Відправляємо запит на сервер
        const response = await accessAPI.createAccessRequest(this.form);

        console.log("Access request response:", response);

        if (response.status === "done") {
          // Емітуємо подію успішного створення
          this.$emit("success", response);
          this.$toast.success("Запит на доступ успішно надіслано");

          // Закриваємо модалку
          this.close();
        } else {
          switch (response.error[0]) {
            case "ticketId is required":
              this.$toast.error("Потрібно вказати ID тікету");
              break;
            case "workerId is required":
              this.$toast.error("Відсутній менеджер запиту");
              break;
            case "companyId is required":
              this.$toast.error("Поле companyId пусте або не передане");
              break;
            case "critical message not sent":
              this.$toast.error(
                "Помилка при відправці критичного повідомлення"
              );
              break;
            case "accessDuration is required and must be positive number":
              this.$toast.error(
                "Тривалість доступу обов'язкова та має бути додатним числом"
              );
              break;
            default:
              this.$toast.error("Невідома помилка");
              break;
          }

          throw new Error("Невідома помилка при створенні запиту");
        }
      } catch (error) {
        // this.$toast.error("Error submitting access request");
        console.error("Error submitting access request:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Модальне вікно overlay */
.access-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Контейнер модального вікна */
.access-modal-container {
  background: white;
  border-radius: 16px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Заголовок */
.access-modal-header {
  background: #fff;
  padding: 24px 24px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #eff2f7;
}

.access-modal-header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.access-modal-icon-wrapper {
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.access-modal-icon-wrapper i {
  font-size: 20px;
  color: #74788d;
}

.access-modal-header-text {
  flex: 1;
}

.access-modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #495057;
}

.access-modal-code {
  font-size: 12px;
  margin: 0;
  color: #74788d;
  font-weight: 400;
}

.access-modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #74788d;
  flex-shrink: 0;
}

.access-modal-close:hover {
  background: #f8f9fa;
}

.access-modal-close i {
  font-size: 20px;
}

/* Інформаційний блок */
.access-modal-info {
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #eff2f7;
}

.access-modal-info p {
  margin: 0;
  font-size: 13px;
  color: #74788d;
  line-height: 1.5;
}

/* Тіло модального вікна */
.access-modal-body {
  background: white;
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Секція */
.access-section {
  margin-bottom: 20px;
}

.access-section:last-of-type {
  margin-bottom: 0;
}

.access-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.access-section-header i {
  font-size: 16px;
  color: #74788d;
}

.access-section-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #495057;
  flex: 1;
}

.access-optional {
  color: #a6b0cf;
  font-size: 12px;
  font-style: italic;
}

.access-select-all {
  color: #556ee6;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;
}

.access-select-all:hover {
  text-decoration: underline;
}

/* Вибір компанії */
.company-select-list {
  display: flex;
  /* flex-direction: column; */
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding-bottom: 10px;
}

.company-select-item {
  background: #fff;
  border: 2px solid #eff2f7;
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.company-select-item:hover {
  border-color: #556ee6;
  background: #f8f9fe;
  transform: translateX(2px);
}

.company-select-item.active {
  border-color: #556ee6;
  background: #f8f9fe;
  box-shadow: 0 2px 4px rgba(85, 110, 230, 0.2);
}

.company-select-info {
  flex: 1;
}

.company-select-name {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.company-select-name i {
  font-size: 16px;
  color: #556ee6;
}

.company-select-phone {
  font-size: 13px;
  color: #74788d;
  display: flex;
  align-items: center;
  gap: 6px;
}

.company-select-phone i {
  font-size: 14px;
}

.company-select-clients {
  font-size: 12px;
  color: #a6b0cf;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.company-select-clients i {
  font-size: 13px;
  color: #a6b0cf;
}

.company-select-clients span {
  line-height: 1.4;
}

.company-select-check {
  font-size: 24px;
  color: #556ee6;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.company-select-item.active .company-select-check {
  opacity: 1;
}

.company-select-empty {
  text-align: center;
  padding: 30px 20px;
  color: #a6b0cf;
  background: #f8f9fa;
  border-radius: 8px;
}

.company-select-empty i {
  font-size: 40px;
  color: #d6dae0;
  margin-bottom: 10px;
  display: block;
}

.company-select-empty p {
  margin: 0;
  font-size: 13px;
}

/* Список прав доступу */
.access-permissions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.access-permission-item {
  background: #f8f9fa;
  border: 1px solid #eff2f7;
  border-radius: 8px;
  padding: 12px;
}

.access-permission-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.access-permission-header i {
  font-size: 16px;
  color: #556ee6;
}

.access-permission-label {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.access-permission-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.access-permission-options-two {
  grid-template-columns: repeat(2, 1fr);
}

.access-permission-option {
  background: #fff;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  color: #74788d;
}

.access-permission-option:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.access-permission-option.active {
  background: #556ee6;
  border-color: #556ee6;
  color: white;
}

.access-permission-option i {
  font-size: 14px;
}

/* Сітка тривалості */
.access-duration-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.access-duration-card {
  background: #fff;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.access-duration-card:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.access-duration-card.active {
  background: #556ee6;
  border-color: #556ee6;
  color: white;
}

.access-duration-number {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 2px;
}

.access-duration-label {
  font-size: 12px;
  opacity: 0.8;
}

/* Поле коментаря */
.access-comment-input {
  width: 100%;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  background: #fff;
}

.access-comment-input:focus {
  outline: none;
  border-color: #556ee6;
  background: #fff;
}

.access-comment-input::placeholder {
  color: #a6b0cf;
}

/* Попередження */
.access-warning {
  background: #fef5e7;
  border: 1px solid #f8e4c0;
  border-radius: 6px;
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  margin-top: 16px;
  font-size: 12px;
  color: #74788d;
}

.access-warning i {
  font-size: 18px;
  color: #f1b44c;
  flex-shrink: 0;
}

/* Футер */
.access-modal-footer {
  background: white;
  padding: 16px 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #eff2f7;
}

.access-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.access-btn-cancel {
  background: #fff;
  color: #74788d;
  border: 1px solid #eff2f7;
}

.access-btn-cancel:hover {
  background: #f8f9fa;
  border-color: #d6dae0;
}

.access-btn-submit {
  background: #556ee6;
  color: white;
}

.access-btn-submit:hover:not(:disabled) {
  background: #4a5fc1;
}

.access-btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #a6b0cf;
}

/* Адаптивність */
@media (max-width: 768px) {
  .access-modal-container {
    border-radius: 16px;
  }

  .access-permission-options {
    grid-template-columns: 1fr;
  }

  .access-duration-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .access-modal-body {
    padding: 20px;
  }

  .access-modal-footer {
    flex-direction: column;
  }

  .access-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
